import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { activitiesApi, registrationsApi, uploadApi, messagingApi } from '../lib/api';
import { formatDate, formatPrice, getCategoryInfo } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import analytics from '../lib/analytics';
import {
  ChevronLeft,
  Calendar,
  MapPin,
  Clock,
  CreditCard,
  Building2,
  ExternalLink,
  Copy,
  Check,
  AlertCircle,
  CheckCircle2,
  Minus,
  Plus,
  Camera,
  Upload,
  X,
  MessageCircle
} from 'lucide-react';

const Checkout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Review, 2: Payment, 3: Confirmation
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [appliedDiscountAmount, setAppliedDiscountAmount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const proofInputRef = useRef(null);

  // Get selected date from location state if available
  useEffect(() => {
    if (location.state?.selectedDateIndex !== undefined) {
      setSelectedDateIndex(location.state.selectedDateIndex);
    }
  }, [location.state]);

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchActivity = async () => {
    try {
      const response = await activitiesApi.getById(id);
      setActivity(response.data);
    } catch (error) {
      toast.error('Error al cargar la actividad');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSubmitRegistration = async () => {
    if (!user?.email_verified) {
      toast.error('Verifica tu correo electrónico para registrarte');
      return;
    }
    setProcessing(true);
    try {
      const selectedDate = activity.dates[selectedDateIndex];
      const response = await activitiesApi.register(id, { 
        selected_date: selectedDate,
        coupon_code: couponCode || null,
        quantity: quantity
      });
      
      setRegistrationResult(response.data);
      setAppliedDiscount(response.data.discount || 0);
      analytics.registerActivity(id, activity?.price || 0);
      
      if (response.data.requires_payment) {
        setStep(2); // Go to payment step
      } else {
        setStep(3); // Go to confirmation (free activity)
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentComplete = () => {
    setShowUploadModal(true);
  };

  const handleProofFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('La imagen no debe superar 5MB'); return; }
    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
  };

  const handleUploadProof = async () => {
    if (!proofFile) return;
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      const uploadRes = await uploadApi.upload(formData);
      const imageUrl = uploadRes.data.url;
      await messagingApi.sendMessage({
        recipient_id: activity.creator_id,
        activity_id: id,
        content: '📎 Comprobante de pago adjunto',
        image_url: imageUrl,
      });
      setShowUploadModal(false);
      setStep(3);
      toast.success('Comprobante enviado correctamente');
    } catch (error) {
      toast.error('Error al enviar el comprobante. Intenta de nuevo.');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleConfirmLater = async () => {
    if (registrationResult?.registration_id) {
      try {
        await registrationsApi.sendPaymentReminder(registrationResult.registration_id);
      } catch (err) {
        console.warn('Payment reminder non-critical:', err.message);
      }
    }
    // Show 24h warning before leaving
    const deadlineHours = registrationResult?.payment_deadline_hours || 24;
    const confirmed = window.confirm(
      `Tu reserva estará pendiente de pago por ${deadlineHours} horas. Si no confirmas el pago en ese tiempo, tu reserva será eliminada automáticamente.\n\n¿Deseas continuar?`
    );
    if (confirmed) {
      toast.info(`Tienes ${deadlineHours} horas para confirmar tu pago`, { duration: 5000 });
      navigate('/my-activities');
    }
  };

  const handleOpenPaymentLink = () => {
    if (registrationResult?.payment_link) {
      window.open(registrationResult.payment_link, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  const categoryInfo = getCategoryInfo(activity.category);
  const originalPrice = activity.price || 0;
  let unitPriceAfterDiscount = originalPrice * (1 - appliedDiscount / 100);
  if (appliedDiscountAmount > 0) unitPriceAfterDiscount = Math.max(0, unitPriceAfterDiscount - appliedDiscountAmount);
  const finalPrice = unitPriceAfterDiscount * quantity;
  const totalDiscountValue = (originalPrice * quantity) - finalPrice;
  const selectedDate = activity.dates?.[selectedDateIndex] || activity.dates?.[0] || {};
  const isFree = originalPrice === 0;
  const bankInfo = registrationResult?.bank_info || activity.bank_info;
  const paymentLink = registrationResult?.payment_link || activity.payment_link;
  const hasPaymentMethod = bankInfo || paymentLink;
  const spotsLeft = activity.capacity - activity.registered_count;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 safe-top">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-rubik font-semibold">
            {step === 1 ? t('checkout.confirm') : step === 2 ? t('checkout.paymentInstructions') : t('checkout.registered')}
          </h1>
          <div className="w-9" />
        </div>
        
        {/* Progress bar */}
        <div className="max-w-lg mx-auto flex gap-1 px-4 pb-3">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-32">
        {/* Step 1: Review */}
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            {/* Activity summary */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400'} 
                    alt={activity.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-primary font-medium mb-1">
                    {t(`category.${activity.category?.toLowerCase()}`)}
                  </p>
                  <h2 className="font-rubik font-semibold text-gray-500 line-clamp-2 mb-2">
                    {activity.title}
                  </h2>
                  <div className="flex items-center text-sm text-gray-400">
                    <MapPin className="w-4 h-4 mr-1" />
                    {activity.city}
                  </div>
                </div>
              </div>
            </div>

            {/* Date selection */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="font-rubik font-semibold mb-3">Fecha y horario</h3>
              
              {activity.dates.length > 1 ? (
                <div className="space-y-2">
                  {activity.dates.map((dateInfo, idx) => (
                    <button
                      key={dateInfo.date || idx}
                      onClick={() => setSelectedDateIndex(idx)}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        selectedDateIndex === idx 
                          ? 'border-primary bg-primary/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className={`w-5 h-5 ${selectedDateIndex === idx ? 'text-primary' : 'text-gray-400'}`} />
                        <div>
                          <p className="font-medium">{formatDate(dateInfo.date, language === 'es' ? 'es-MX' : 'en-US')}</p>
                          <p className="text-sm text-gray-400">{dateInfo.time} {dateInfo.end_time && `- ${dateInfo.end_time}`}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3 text-gray-500">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium">{formatDate(selectedDate?.date, language === 'es' ? 'es-MX' : 'en-US')}</p>
                    <p className="text-sm text-gray-400">{selectedDate?.time} {selectedDate?.end_time && `- ${selectedDate.end_time}`}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Your info */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="font-rubik font-semibold mb-3">{t('checkout.quantity')}</h3>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  {spotsLeft > 0 ? `${spotsLeft} ${t('checkout.spotsAvailable')}` : t('checkout.noSpots')}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-30 hover:border-gray-400 transition-colors"
                    data-testid="quantity-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-rubik font-semibold text-lg w-8 text-center" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.min(q + 1, Math.min(spotsLeft, 20)))}
                    disabled={quantity >= Math.min(spotsLeft, 20)}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-30 hover:border-gray-400 transition-colors"
                    data-testid="quantity-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* User info */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="font-rubik font-semibold mb-3">{t('checkout.yourInfo')}</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-400">Nombre:</span> {user?.name}</p>
                <p><span className="text-gray-400">Email:</span> {user?.email}</p>
                {user?.phone && <p><span className="text-gray-400">Teléfono:</span> {user?.phone}</p>}
              </div>
            </div>

            {/* Coupon code */}
            {!isFree && (
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <h3 className="font-rubik font-semibold mb-3">Cupón de descuento</h3>
                {couponApplied ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        {couponCode} aplicado
                        {appliedDiscountAmount > 0 && ` (-$${appliedDiscountAmount} MXN)`}
                        {appliedDiscount > 0 && ` (-${appliedDiscount}%)`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCouponApplied(false); setCouponCode(''); setAppliedDiscount(0); setAppliedDiscountAmount(0); }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                      data-testid="remove-coupon-btn"
                    >
                      Quitar
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                      placeholder="Código de cupón"
                      className="flex-1 uppercase"
                      data-testid="coupon-input"
                    />
                    <Button
                      variant="outline"
                      disabled={!couponCode || couponLoading}
                      onClick={async () => {
                        setCouponLoading(true);
                        try {
                          const res = await activitiesApi.validateCoupon(id, couponCode);
                          setAppliedDiscount(res.data.discount_percent || 0);
                          setAppliedDiscountAmount(res.data.discount_amount || 0);
                          setCouponApplied(true);
                          toast.success('Cupón aplicado');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Cupón no válido');
                        } finally {
                          setCouponLoading(false);
                        }
                      }}
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? '...' : 'Aplicar'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation policy */}
            {(activity.cancellation_rules?.rules?.length > 0 || activity.cancellation_policy) && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-800 mb-2">Política de cancelación</h3>
                    {activity.cancellation_rules?.rules?.length > 0 && (
                      <ul className="text-sm text-amber-700 space-y-0.5 mb-2">
                        {[...(activity.cancellation_rules.rules || [])].sort((a, b) => b.min_hours - a.min_hours).map((r, i) => (
                          <li key={`refund-${r.min_hours}`}>• Más de {r.min_hours}h antes → {r.refund_percent}% de reembolso</li>
                        ))}
                        <li>• Menos de {activity.cancellation_rules.min_cancel_hours || 24}h antes → Sin reembolso</li>
                      </ul>
                    )}
                    {activity.cancellation_policy && (
                      <p className="text-sm text-amber-700 italic">{activity.cancellation_policy}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price summary */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="font-rubik font-semibold mb-3">{t('checkout.summary')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('checkout.pricePerPerson')}</span>
                  <span>{isFree ? 'Gratis' : formatPrice(originalPrice, 'es-MX')}</span>
                </div>
                {quantity > 1 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{t('checkout.quantity_label')}</span>
                    <span>x{quantity}</span>
                  </div>
                )}
                {(appliedDiscount > 0 || appliedDiscountAmount > 0) && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento ({couponCode})</span>
                    <span>-{formatPrice(totalDiscountValue, 'es-MX')}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between font-semibold">
                    <span>{t('checkout.total')}</span>
                    <span className={isFree ? 'text-primary' : ''}>
                      {isFree ? 'Gratis' : formatPrice(finalPrice, 'es-MX')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && registrationResult && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="font-rubik text-xl font-bold mb-2">Pendiente de pago</h2>
              <p className="text-gray-400">
                Tu reserva está pendiente. Realiza el pago para confirmarla.
              </p>
            </div>

            {/* 24h deadline warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-700 font-medium">
                Tienes 24 horas para realizar tu pago. Si no se confirma en este plazo, tendrás 24 horas adicionales de gracia. Después de 48 horas, tu reserva será cancelada automáticamente y tu lugar quedará disponible para otros usuarios.
              </p>
            </div>

            {/* Amount to pay */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 text-center">
              <p className="text-sm text-gray-400 mb-1">Total a pagar</p>
              <p className="font-rubik text-3xl font-bold text-primary">
                {formatPrice(registrationResult.final_price, 'es-MX')}
              </p>
            </div>

            {/* Payment methods */}
            <div className="space-y-4">
              <h3 className="font-rubik font-semibold">Elige cómo pagar</h3>
              
              {/* Payment link option */}
              {paymentLink && (
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Pago en línea</p>
                      <p className="text-sm text-gray-400">Paga con tarjeta o transferencia</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleOpenPaymentLink}
                    className="w-full bg-primary hover:bg-primary-hover rounded-xl"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ir a pagar
                  </Button>
                </div>
              )}

              {/* Bank transfer option */}
              {bankInfo && (
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Transferencia bancaria</p>
                      <p className="text-sm text-gray-400">Deposita a la cuenta del organizador</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                    {bankInfo.bank_name && (
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-400">Banco</p>
                          <p className="font-medium">{bankInfo.bank_name}</p>
                        </div>
                      </div>
                    )}
                    {bankInfo.account_holder && (
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-400">Titular</p>
                          <p className="font-medium">{bankInfo.account_holder}</p>
                        </div>
                        <button 
                          onClick={() => handleCopyToClipboard(bankInfo.account_holder, 'holder')}
                          className="p-2 rounded-lg hover:bg-gray-200"
                        >
                          {copiedField === 'holder' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                        </button>
                      </div>
                    )}
                    {bankInfo.clabe && (
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-400">CLABE</p>
                          <p className="font-mono font-medium">{bankInfo.clabe}</p>
                        </div>
                        <button 
                          onClick={() => handleCopyToClipboard(bankInfo.clabe, 'clabe')}
                          className="p-2 rounded-lg hover:bg-gray-200"
                        >
                          {copiedField === 'clabe' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                        </button>
                      </div>
                    )}
                    {bankInfo.account_number && !bankInfo.clabe && (
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-400">Número de cuenta</p>
                          <p className="font-mono font-medium">{bankInfo.account_number}</p>
                        </div>
                        <button 
                          onClick={() => handleCopyToClipboard(bankInfo.account_number, 'account')}
                          className="p-2 rounded-lg hover:bg-gray-200"
                        >
                          {copiedField === 'account' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                        </button>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-400">Monto</p>
                        <p className="font-mono font-bold text-primary">{formatPrice(registrationResult.final_price, 'es-MX')}</p>
                      </div>
                      <button 
                        onClick={() => handleCopyToClipboard(registrationResult.final_price.toString(), 'amount')}
                        className="p-2 rounded-lg hover:bg-gray-200"
                      >
                        {copiedField === 'amount' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Reference Code */}
            {registrationResult?.payment_ref_code && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 font-medium mb-1">Referencia de pago</p>
                    <p className="font-mono font-bold text-lg text-amber-800">{registrationResult.payment_ref_code}</p>
                    <p className="text-xs text-amber-500 mt-1">Usa este código como referencia en tu pago</p>
                  </div>
                  <button 
                    onClick={() => handleCopyToClipboard(registrationResult.payment_ref_code, 'ref')}
                    className="p-2.5 rounded-lg hover:bg-amber-100 transition-colors"
                    data-testid="copy-ref-code"
                  >
                    {copiedField === 'ref' ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-amber-600" />}
                  </button>
                </div>
              </div>
            )}

            {/* Time-sensitive warning */}
            {registrationResult?.payment_warning === '6h_3h' && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200" data-testid="warning-6h-3h">
                <p className="text-sm text-red-700">
                  <strong>La actividad inicia muy pronto.</strong> Tienes <strong>3 horas</strong> para realizar tu pago y subir tu comprobante. Si no se confirma a tiempo, tu reserva será cancelada.
                </p>
              </div>
            )}
            {registrationResult?.payment_warning === '24h_6h' && (
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200" data-testid="warning-24h-6h">
                <p className="text-sm text-orange-700">
                  <strong>La actividad inicia en menos de 24 horas.</strong> Tienes <strong>6 horas</strong> para realizar tu pago y subir tu comprobante, de lo contrario tu reserva será cancelada automáticamente.
                </p>
              </div>
            )}
            {registrationResult?.payment_warning === '48h_24h' && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-testid="warning-48h-24h">
                <p className="text-sm text-amber-700">
                  <strong>La actividad inicia pronto.</strong> Tienes <strong>20 horas</strong> para realizar tu pago y subir tu comprobante, de lo contrario tu reserva será cancelada automáticamente.
                </p>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Importante:</strong> Una vez realizado el pago, necesitas subir tu comprobante para confirmar tu pago. Recibirás una notificación cuando tu lugar esté confirmado por el organizador de la actividad.
              </p>
            </div>

            <Button 
              onClick={handlePaymentComplete}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl py-6 font-semibold"
              data-testid="payment-complete-btn"
            >
              Confirmar pago con comprobante
            </Button>

            <Button 
              onClick={handleConfirmLater}
              variant="ghost"
              className="w-full rounded-xl py-6 text-gray-400 hover:text-gray-500"
              data-testid="confirm-later-btn"
            >
              Confirmar más tarde y salir
            </Button>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in text-center">
            <div className="py-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-rubik text-2xl font-bold mb-2">
                {registrationResult?.requires_payment ? '¡Reserva registrada!' : '¡Reserva confirmada!'}
              </h2>
              <p className="text-gray-400">
                {registrationResult?.requires_payment 
                  ? 'Tu reserva está pendiente de confirmación de pago por el organizador.'
                  : '¡Te esperamos! Hemos guardado tu lugar.'}
              </p>
            </div>

            {/* Activity summary */}
            <div className="bg-white rounded-xl p-4 border border-gray-100 text-left">
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400'} 
                    alt={activity.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-rubik font-semibold mb-1">{activity.title}</h3>
                  <p className="text-sm text-gray-400">
                    {formatDate(selectedDate?.date, language === 'es' ? 'es-MX' : 'en-US')}
                  </p>
                  <p className="text-sm text-gray-400">{selectedDate?.time} {selectedDate?.end_time && `- ${selectedDate.end_time}`}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-4">
              <Button 
                onClick={() => navigate(`/messages?to=${activity.creator_id}&activity=${id}`)}
                className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6"
                data-testid="view-chat-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Ver chat actividad
              </Button>
              <Button 
                onClick={() => navigate('/my-activities')}
                variant="outline"
                className="w-full rounded-xl py-6"
              >
                Ver mis reservas
              </Button>
              <Button 
                onClick={() => navigate('/prelanzamiento')}
                variant="ghost"
                className="w-full rounded-xl py-6 text-gray-400 hover:text-gray-500"
              >
                Seguir explorando
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Fixed bottom button for step 1 */}
      {step === 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 safe-bottom">
          <div className="max-w-lg mx-auto">
            <Button 
              onClick={handleSubmitRegistration}
              disabled={processing}
              className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
            >
              {processing ? t('msg.loading') : (isFree ? t('checkout.confirm') : `${t('checkout.confirm')} ${formatPrice(finalPrice, language === 'es' ? 'es-MX' : 'en-US')}`)}
            </Button>
          </div>
        </div>
      )}

      {/* Upload proof modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md p-0 rounded-2xl overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-0">
            <DialogTitle className="font-rubik">Subir comprobante de pago</DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-5 space-y-4">
            <p className="text-sm text-gray-400">Adjunta una captura o foto de tu comprobante de pago.</p>
            {proofPreview ? (
              <div className="relative">
                <img src={proofPreview} alt="Comprobante" className="w-full max-h-64 object-contain rounded-xl border border-gray-200 bg-gray-50" />
                <button onClick={() => { setProofFile(null); setProofPreview(null); if (proofInputRef.current) proofInputRef.current.value = ''; }} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ) : (
              <button onClick={() => proofInputRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-xl py-8 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-all" data-testid="checkout-upload-area">
                <Camera className="w-8 h-8 text-gray-400" />
                <p className="text-sm font-medium text-gray-600">Toca para subir imagen</p>
                <p className="text-xs text-gray-300">JPG, PNG o WEBP · Máx 5MB</p>
              </button>
            )}
            <input ref={proofInputRef} type="file" accept="image/*" className="hidden" onChange={handleProofFileSelect} />
            <Button onClick={handleUploadProof} disabled={!proofFile || uploadingProof} className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-12 font-semibold disabled:opacity-40" data-testid="checkout-send-proof-btn">
              {uploadingProof ? (
                <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Enviando...</span>
              ) : (
                <span className="flex items-center gap-2"><Upload className="w-4 h-4" />Enviar comprobante</span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Checkout;
